import {
  ContentDetailCard,
  deserify,
  getMediaBasePath,
  getResourceCategory,
  resourceTypeName,
  shouldIncludeCredentialsInRequest,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { Box, Button, Typography } from '@mui/material';
import { JsonValue } from '@bufbuild/protobuf';
import { ResourceContent as ResourceContentMeta } from '@protos/content_management/content.common.apis_pb';
import {
  Question,
  Resource_ResourceEnum,
  ResourceCategoryEnum,
} from '@protos/content_management/content.db_pb';
import {
  QuestionSectionMeta,
  SectionQuestionMetaData,
  SessionModeEnum,
} from '@protos/learning_management/lms.db_pb';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PrintableQuestionPaper from '../Assessment/QuestionPaperGenerator/ReviewQuestionPaper/PrintableQuestionPaper';
import { setToastInfo } from '../Home/reducer/homeDashboard.slice';
import { setResourceRedirectionPath } from '../Teach/reducer/teach.slice';
import { useAppSelector } from '../../reduxStore/reduxHooks';
import { DEMO_RESOURCE_QPG_DOWNLOAD } from '../../routeHandling/RoutesNomenclature';
import { onResourceClick } from '../../utils/resource';

const fieldSx = {
  width: '100%',
  marginBottom: '10px',
};

type JsonRecord = Record<string, unknown>;
type RawResourcePage = {
  questionIds?: string[];
  contents?: Array<{ question?: unknown }>;
};

const toProtoJson = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(toProtoJson);
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as JsonRecord).map(([key, nested]) => [
        key.includes('_')
          ? key.replace(/_([a-z0-9])/g, (_, ch: string) => ch.toUpperCase())
          : key,
        toProtoJson(nested),
      ])
    );
  }
  return value;
};

const questionHasBody = (question?: Question) =>
  Boolean(question?.question?.model.case);

const parseQuestionFromRaw = (raw: unknown): Question | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const converted = toProtoJson(raw) as JsonRecord;
  const tryParse = (json: JsonValue) => {
    const question = new Question().fromJson(json, {
      ignoreUnknownFields: true,
    });
    return question.questionId ? question : undefined;
  };
  try {
    const question = tryParse(converted as JsonValue);
    if (question && questionHasBody(question)) return question;
  } catch {
    // Retry with only the fields PrintableQuestion needs.
  }
  try {
    return tryParse({
      questionId: converted.questionId,
      questionType: converted.questionType,
      question: converted.question,
      questionText: converted.questionText,
      title: converted.title,
    } as JsonValue);
  } catch {
    return undefined;
  }
};

const getQuestionsFromGcpJson = (gcpJson: unknown): Question[] => {
  const pages =
    (gcpJson as { resourceContentData?: { pageContent?: RawResourcePage[] } })
      ?.resourceContentData?.pageContent ?? [];
  const questions: Question[] = [];
  const seen = new Set<string>();

  for (const page of pages) {
    for (const content of page.contents ?? []) {
      const question = parseQuestionFromRaw(content.question);
      if (question?.questionId && !seen.has(question.questionId)) {
        seen.add(question.questionId);
        questions.push(question);
      }
    }
    for (const questionId of page.questionIds ?? []) {
      if (!seen.has(questionId)) {
        seen.add(questionId);
        questions.push(new Question({ questionId }));
      }
    }
  }
  return questions;
};

export default function DemoResourceQpgDownload() {
  const [classGrade, setClassGrade] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [subject, setSubject] = useState('');
  const [assessmentName, setAssessmentName] = useState('');
  const [resourceId, setResourceId] = useState('');
  const [resourceInfo, setResourceInfo] = useState<
    ResourceContentMeta | undefined
  >();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [printAnswer, setPrintAnswer] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [loading, setLoading] = useState(false);
  const printablePaperRef = useRef<{
    handlePrint: (
      isAnswer?: boolean,
      title?: string,
      printAsDoc?: boolean
    ) => Promise<void>;
  } | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const teacherId = user_info?.teacherProfileId;
  const { ContentCommonAPIServiceV1ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();

  const fetchResourceData = async () => {
    if (!resourceId) return;
    try {
      setLoading(true);
      setQuestions([]);
      const res =
        await ContentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchResourceContent(
          {
            personId: teacherId,
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            resourceId,
          }
        );
      if (!res?.data) {
        setResourceInfo(undefined);
        return;
      }
      const resource = new ResourceContentMeta(res.data);
      setResourceInfo(resource);
      if (!resource.gcpJsonUrl) {
        dispatch(
          setToastInfo({
            label: 'Questions not available, so PDF cannot be generated',
            variant: 'error',
            open: true,
          })
        );
        return;
      }
      const isUserDataBucket =
        resource.resourceCategory ===
        ResourceCategoryEnum.RESOURCE_CATEGORY_MY_RESOURCES;
      const mediaUrl = getMediaBasePath(
        resource.gcpJsonUrl,
        isUserDataBucket ? 'userDataBucket' : 'resourceContentBucket'
      );
      const options: RequestInit = {};
      if (shouldIncludeCredentialsInRequest(mediaUrl)) {
        options.credentials = 'include';
      }
      const gcpResponse = await (await fetch(mediaUrl, options)).json();
      const questionsFromJson = getQuestionsFromGcpJson(gcpResponse);
      const missingIds = questionsFromJson
        .filter((question) => !questionHasBody(question))
        .map((question) => question.questionId)
        .filter((questionId): questionId is string => Boolean(questionId));
      const geneoQuestionRes = missingIds.length
        ? await ContentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchQuestions(
            {
              questionIds: missingIds,
            }
          )
        : undefined;
      const fetchedById = new Map(
        (geneoQuestionRes?.data?.questions ?? []).map((question) => [
          question.questionId,
          question,
        ])
      );
      const loadedQuestions = questionsFromJson
        .map((question) =>
          questionHasBody(question)
            ? question
            : fetchedById.get(question.questionId) ?? question
        )
        .filter(questionHasBody);
      setQuestions(loadedQuestions);
      if (!loadedQuestions.length) {
        dispatch(
          setToastInfo({
            label: 'Questions not available, so PDF cannot be generated',
            variant: 'error',
            open: true,
          })
        );
      }
    } catch (err) {
      setResourceInfo(undefined);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const openResource = () => {
    dispatch(setResourceRedirectionPath(DEMO_RESOURCE_QPG_DOWNLOAD));
    onResourceClick(
      navigate,
      { resourceId },
      SessionModeEnum.SESSION_MODE_UNDEFINED,
      'view'
    );
  };

  const downloadPaper = (isAnswer: boolean, printAsDoc = false) => {
    setPrintAnswer(isAnswer);
    setIsDownloading(true);
    setTimeout(async () => {
      try {
        await printablePaperRef.current?.handlePrint(
          isAnswer,
          undefined,
          printAsDoc
        );
      } finally {
        setIsDownloading(false);
      }
    }, 0);
  };

  return (
    <Box
      sx={{
        margin: '20px',
        pointerEvents: loading || isDownloading ? 'none' : 'auto',
        opacity: loading || isDownloading ? 0.5 : 1,
      }}
    >
      School name:
      <Box
        component="input"
        type="text"
        value={schoolName}
        onChange={(ev) => setSchoolName(ev.target.value)}
        sx={fieldSx}
      />
      Class / Grade:
      <Box
        component="input"
        type="text"
        value={classGrade}
        onChange={(ev) => setClassGrade(ev.target.value)}
        sx={fieldSx}
      />
      Subject:
      <Box
        component="input"
        type="text"
        value={subject}
        onChange={(ev) => setSubject(ev.target.value)}
        sx={fieldSx}
      />
      Assessment name:
      <Box
        component="input"
        type="text"
        value={assessmentName}
        onChange={(ev) => setAssessmentName(ev.target.value)}
        sx={fieldSx}
      />
      Resource:
      <Box
        component="input"
        type="text"
        value={resourceId}
        onChange={(ev) => setResourceId(ev.target.value)}
        sx={fieldSx}
      />
      <Button variant="contained" color="secondary" onClick={fetchResourceData}>
        Submit
      </Button>
      <Box sx={{ margin: '20px 0' }}>
        {resourceInfo ? (
          <Box width="100%">
            <Typography
              variant="h3"
              sx={{
                width: '100%',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
              }}
            >
              <b>Resource Id:</b> {resourceInfo.resourceId}
            </Typography>
            <br />
            Click the card to open the resource.
            <br />
            <br />
            <ContentDetailCard
              variant="large"
              tagName={getResourceCategory(resourceInfo.resourceCategory)}
              image={getMediaBasePath(
                resourceInfo.posterImageUrl,
                'processedMediaBucket'
              )}
              heading={resourceInfo.title}
              onClick={openResource}
              iconDetails={[
                {
                  iconName: 'clock',
                  text: resourceInfo.estimatedTimeInMin + ' Min',
                },
                {
                  iconName: resourceTypeName(resourceInfo.resourceType)?.icon,
                  text: resourceTypeName(resourceInfo.resourceType)?.name,
                },
              ]}
              showVideoIcon={
                resourceInfo.resourceType ===
                Resource_ResourceEnum.RESOURCE_TYPE_VIDEO
              }
            />
            {questions.length > 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                  mt: '16px',
                  maxWidth: 360,
                }}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => downloadPaper(false)}
                >
                  Download Question Paper (PDF)
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => downloadPaper(true)}
                >
                  Download Answer Paper (PDF)
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => downloadPaper(false, true)}
                >
                  Download Question Paper (Doc)
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => downloadPaper(true, true)}
                >
                  Download Answer Paper (Doc)
                </Button>
              </Box>
            ) : null}
            {questions.length > 0 ? (
            <PrintableQuestionPaper
              questionSectionMetaInfo={[
                new QuestionSectionMeta({
                  sectionQuestionsInfo: questions.map(
                    (ques, index) =>
                      new SectionQuestionMetaData({
                        questionId: ques.questionId,
                        sequence: index,
                        showMedia: true,
                      })
                  ),
                }),
              ]}
              sectionMetaStatements={[]}
              questionsList={questions}
              printAnswer={printAnswer}
              ref={printablePaperRef}
              instructions={[]}
              loading={loading || isDownloading}
              setLoading={setLoading}
              schoolName={schoolName}
              assessmentTitle={assessmentName.trim() || resourceInfo.title}
              grade={classGrade}
              subject={subject}
            />
            ) : null}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
